import { useEffect, lazy, Suspense } from 'react';
import { captureReferral } from './utils/referral';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import toast, { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './context/ThemeContext';
import { useAuthStore } from './context/useAuthStore';
import { check_is_active } from './context/health';
import { usePresenceHeartbeat } from './hooks/usePresenceHeartbeat';
import ErrorBoundary from './components/ErrorBoundary';
import Seo from './components/Seo';
import Layout from './components/layout/Layout';
import LoadingScreen from './components/ui/LoadingScreen';

import Home from './pages/Home';

const Shop = lazy(() => import('./pages/Shop'));
const BookDetails = lazy(() => import('./pages/BookDetails'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Login = lazy(() => import('./pages/Login'));
const AuthorLogin = lazy(() => import('./pages/AuthorLogin'));
const Signup = lazy(() => import('./pages/Signup'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Account = lazy(() => import('./pages/Account'));
const ReferEarn = lazy(() => import('./pages/ReferEarn'));
const AffiliateDashboard = lazy(() => import('./pages/AffiliateDashboard'));
const Rewards = lazy(() => import('./pages/Rewards'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Reader = lazy(() => import('./pages/Reader'));
const Authors = lazy(() => import('./pages/Authors'));
const AuthorProfile = lazy(() => import('./pages/AuthorProfile'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const About = lazy(() => import('./pages/About'));
const Support = lazy(() => import('./pages/Support'));
const Contact = lazy(() => import('./pages/Contact'));
const Membership = lazy(() => import('./pages/Membership'));
const Publish = lazy(() => import('./pages/Publish'));
const PublishDashboard = lazy(() => import('./pages/PublishDashboard'));
const EditBook = lazy(() => import('./pages/EditBook'));
const Affiliate = lazy(() => import('./pages/Affiliate'));
const PrivacyPage = lazy(() => import('./pages/Privacy'));
const TermsPage = lazy(() => import('./pages/Terms'));
const RefundsPage = lazy(() => import('./pages/Refunds'));
const CookiesPage = lazy(() => import('./pages/Cookies'));
const DMCA = lazy(() => import('./pages/DMCA'));
const ShippingPage = lazy(() => import('./pages/Shipping'));
const Sitemap = lazy(() => import('./pages/Sitemap'));
const Library = lazy(() => import('./pages/Library'));
const SampleViewer = lazy(() => import('./pages/SampleViewer'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Protect routes that require authentication.
 *
 * If the user is not logged in, remember the current URL
 * and send them to login.
 */
function RequireAuth({ children }) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const user = useAuthStore((s) => s.user);

  if (!isInitialized) {
    return (
      <div className="relative min-h-[60vh]">
        <LoadingScreen fullScreen={false} />
      </div>
    );
  }

  if (!user) {
    const currentUrl =
      window.location.pathname +
      window.location.search +
      window.location.hash;

    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: currentUrl,
        }}
      />
    );
  }

  return children;
}

/**
 * Handles an expired authenticated session.
 */
function SessionExpiryWatcher() {
  const navigate = useNavigate();

  useEffect(() => {
    function handleExpired() {
      const { user, isInitialized } =
        useAuthStore.getState();

      const wasLoggedIn = !!user;

      useAuthStore.setState({
        user: null,
        token: null,
        isInitialized: true,
      });

      // Don't redirect anonymous users during the initial auth check.
      if (!isInitialized) return;

      if (wasLoggedIn) {
        toast.error(
          'Your session has expired. Please log in again.'
        );

        /*
         * Remember the page where the session expired.
         * This allows the user to return to that page
         * after logging in again.
         */
        const currentUrl =
          window.location.pathname +
          window.location.search +
          window.location.hash;

        navigate('/login', {
          replace: true,
          state: {
            from: currentUrl,
          },
        });
      }
    }

    window.addEventListener(
      'auth:session-expired',
      handleExpired
    );

    return () => {
      window.removeEventListener(
        'auth:session-expired',
        handleExpired
      );
    };
  }, [navigate]);

  return null;
}

export default function App() {
  useEffect(() => {
    check_is_active();
    captureReferral();
  }, []);

  usePresenceHeartbeat();

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <BrowserRouter>
            <Toaster
              position="top-center"
              toastOptions={{
                style: {
                  borderRadius: '999px',
                  background: '#1e1b4b',
                  color: '#fff',
                  fontSize: '14px',
                },
              }}
            />

            <SessionExpiryWatcher />

            <Seo />

            <Suspense
              fallback={
                <LoadingScreen message="Loading..." />
              }
            >
              <Routes>
                {/* Reader */}
                <Route
                  path="/read/:bookId"
                  element={
                    <RequireAuth>
                      <Reader />
                    </RequireAuth>
                  }
                />

                {/* Sample viewer */}
                <Route
                  path="/sample/:bookId"
                  element={<SampleViewer />}
                />

                <Route element={<Layout />}>
                  {/* Home */}
                  <Route
                    index
                    element={<Home />}
                  />

                  {/* Public */}
                  <Route
                    path="shop"
                    element={<Shop />}
                  />

                  <Route
                    path="book/:slug"
                    element={<BookDetails />}
                  />

                  {/* Authentication */}
                  <Route
                    path="login"
                    element={<Login />}
                  />

                  <Route
                    path="author/login"
                    element={<AuthorLogin />}
                  />

                  <Route
                    path="signup"
                    element={<Signup />}
                  />

                  <Route
                    path="forgot-password"
                    element={<ForgotPassword />}
                  />

                  {/* Protected */}
                  <Route
                    path="account"
                    element={
                      <RequireAuth>
                        <Account />
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="account/refer-earn"
                    element={
                      <RequireAuth>
                        <ReferEarn />
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="account/affiliate"
                    element={
                      <RequireAuth>
                        <AffiliateDashboard />
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="account/rewards"
                    element={
                      <RequireAuth>
                        <Rewards />
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="account/wishlist"
                    element={
                      <RequireAuth>
                        <Wishlist />
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="library"
                    element={
                      <RequireAuth>
                        <Library />
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="checkout"
                    element={
                      <RequireAuth>
                        <Checkout />
                      </RequireAuth>
                    }
                  />

                  {/* Public pages */}
                  <Route
                    path="cart"
                    element={<Cart />}
                  />

                  <Route
                    path="authors"
                    element={<Authors />}
                  />

                  <Route
                    path="authors/:slug"
                    element={<AuthorProfile />}
                  />

                  <Route
                    path="blog"
                    element={<Blog />}
                  />

                  <Route
                    path="blog/:slug"
                    element={<BlogPost />}
                  />

                  <Route
                    path="about"
                    element={<About />}
                  />

                  <Route
                    path="support"
                    element={<Support />}
                  />

                  <Route
                    path="contact"
                    element={<Contact />}
                  />

                  <Route
                    path="membership"
                    element={<Membership />}
                  />

                  <Route
                    path="publish"
                    element={<Publish />}
                  />

                  <Route
                    path="publish/dashboard"
                    element={<PublishDashboard />}
                  />

                  <Route
                    path="publish/edit/:id"
                    element={<EditBook />}
                  />

                  <Route
                    path="affiliate"
                    element={<Affiliate />}
                  />

                  {/* Legal */}
                  <Route
                    path="legal/privacy"
                    element={<PrivacyPage />}
                  />

                  <Route
                    path="legal/terms"
                    element={<TermsPage />}
                  />

                  <Route
                    path="legal/refund-policy"
                    element={<RefundsPage />}
                  />

                  <Route
                    path="legal/cookies"
                    element={<CookiesPage />}
                  />

                  <Route
                    path="legal/dmca"
                    element={<DMCA />}
                  />

                  <Route
                    path="legal/shipping-policy"
                    element={<ShippingPage />}
                  />

                  <Route
                    path="legal/sitemap"
                    element={<Sitemap />}
                  />

                  <Route
                    path="unsubscribe"
                    element={<Unsubscribe />}
                  />

                  {/* 404 */}
                  <Route
                    path="*"
                    element={<NotFound />}
                  />
                </Route>
              </Routes>
            </Suspense>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}